/**
 * LiveSessionsListPage.jsx — Diplomexa Live Classroom — Sessions list.
 *
 * Doubles as:
 *   - the standalone "/dashboard/lms/live-sessions" landing page
 *   - the "Live Sessions" tab inside an LMS course (when `courseId` prop set)
 *
 * Provider names are NEVER shown — sessions are just "Diplomexa Live Classroom".
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import { listLiveSessions, formatApiError } from "../../lib/liveClassApi";
import { toast } from "sonner";
import {
  Radio, ArrowRight, Sparkles, Clock, Users, Award, CalendarRange,
  CheckCircle2, GraduationCap, Plus,
} from "lucide-react";

function statusBadge(s) {
  switch (s) {
    case "live":     return { label: "Live now", cls: "bg-rose-100 text-rose-700 ring-rose-300" };
    case "scheduled":return { label: "Upcoming", cls: "bg-blue-100 text-[#0F4C81] ring-blue-200" };
    case "ended":    return { label: "Ended",    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" };
    case "cancelled":return { label: "Cancelled",cls: "bg-slate-100 text-slate-600 ring-slate-300" };
    default:         return { label: s || "—",   cls: "bg-slate-100 text-slate-600 ring-slate-300" };
  }
}

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LiveSessionsListPage({ embedded = false, courseId = null }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const navigate = useNavigate();

  const reload = async () => {
    setLoading(true);
    try {
      const data = await listLiveSessions(
        courseId ? { course_id: courseId, upcoming_only: filter === "upcoming" }
                 : { upcoming_only: filter === "upcoming" },
      );
      setSessions(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [filter, courseId]);

  const Body = (
    <div data-testid="lmslive-sessions-list" className="space-y-5">
      {!embedded && (
        <section
          data-testid="lmslive-hero"
          className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
        >
          <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl" />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
              <Radio size={12} /> Diplomexa Live Classroom
            </p>
            <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
              Global Learning Room
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-blue-100">
              Join live, recorded training sessions led by global diplomats and senior chairs.
              Premium tools — attendance certificates, parent reports & AI summaries — use Diploks.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/dashboard/lms/live-sessions/scheduler"
                data-testid="lmslive-cta-scheduler"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#D4AF37] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A192F] hover:bg-[#c69f30]"
              >
                <Plus size={11} /> Schedule a session
              </Link>
              <Link
                to="/dashboard/lms"
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20"
              >
                <GraduationCap size={11} /> Learning Academy
              </Link>
            </div>
          </div>
        </section>
      )}

      <div className="flex items-center justify-between gap-3">
        <div data-testid="lmslive-filter-tabs" className="inline-flex gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          {["upcoming", "all"].map(k => (
            <button
              key={k}
              type="button"
              data-testid={`lmslive-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`rounded-lg px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition ${
                filter === k ? "bg-[#0A192F] text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={reload}
          data-testid="lmslive-refresh"
          className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0F4C81] hover:text-[#0A192F]"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <LoadingSkeleton variant="cards" count={6} testId="live-sessions-loading" />
      ) : sessions.length === 0 ? (
        <div data-testid="lmslive-empty" className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center">
          <Radio size={28} className="mx-auto text-[#0F4C81]" />
          <p className="mt-2 text-sm font-semibold text-[#0A192F]">No live sessions yet</p>
          <p className="mt-1 text-xs text-slate-500">
            Schedule a session from the Trainer Panel or check back when one is added.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {sessions.map(s => {
            const badge = statusBadge(s.status);
            return (
              <button
                key={s.id}
                type="button"
                data-testid={`lmslive-card-${s.id}`}
                onClick={() => navigate(`/dashboard/lms/live-sessions/${s.id}`)}
                className="group text-left rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:border-[#D4AF37]/60 hover:-translate-y-0.5 transition"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold flex items-center gap-1">
                    <Radio size={11} /> Live Classroom
                  </p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${badge.cls}`}>
                    {badge.label}
                  </span>
                </div>
                <h3 className="mt-2 text-base font-bold text-[#0A192F] truncate">{s.title}</h3>
                {s.description ? (
                  <p className="mt-1 text-xs text-slate-500 line-clamp-2">{s.description}</p>
                ) : null}
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    <CalendarRange size={11} className="text-[#0F4C81]" />
                    {fmtDate(s.start_time)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} className="text-[#0F4C81]" />
                    {Math.max(0, Math.round(
                      (new Date(s.end_time) - new Date(s.start_time)) / 60000,
                    ))}{" "}min
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users size={11} className="text-[#0F4C81]" />
                    {(s.enrolled_user_ids || []).length} learners
                  </span>
                  <span className="inline-flex items-center gap-1">
                    {s.recording_enabled ? (
                      <>
                        <Sparkles size={11} className="text-[#D4AF37]" />
                        Recorded
                      </>
                    ) : (
                      <>
                        <Award size={11} className="text-slate-400" />
                        No recording
                      </>
                    )}
                  </span>
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F4C81] group-hover:text-[#0A192F]">
                  {s.can_join ? "Join classroom" : "View details"} <ArrowRight size={11} />
                </div>
                {s.recording?.status === "ready" ? (
                  <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <CheckCircle2 size={10} /> Recording available
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (embedded) return Body;
  return <DashboardLayout title="Live Classroom">{Body}</DashboardLayout>;
}
